import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  MobileNotificationPopoverContents,
  NotificationPopoverContents,
  getNotificationsProps,
} from '../notificationcenter/NotificationCenter';
import {
  DISMISS_NOTIFICATIONS_MODAL_ICON,
  initNotificationPopovers,
  NOTIFICATIONS_MOBILE_POPOVER,
  NOTIFICATIONS_POPOVER,
} from './newNotifications';
import { AppWindow } from '../../definitions/AppWindow';
declare let window: AppWindow;

export const NOTIFICATION_CENTER_BADGE_CACHE_KEY = 'PP_NC';

async function executeNotificationCenterFetch() {
  let userCancelledLoad = false;

  try {
    window.addEventListener('beforeunload', () => {
      userCancelledLoad = true;
    });
    return await fetch('/myaccount/summary/notificationcenter');
  } catch (error) {
    if (!userCancelledLoad) {
      // Safari noisily reports fetch failure even when user navigates to another page
      console.error(new Error('Fetch notifications failed: No response', { cause: error }));
    }
    return null;
  }
}

export async function fetchNotificationCenter() {
  // fetch data
  const response = await executeNotificationCenterFetch();
  if (!response) {
    return null;
  }

  // check response
  if (!response.ok) {
    console.error(new Error(`Fetch notifications failed: status ${response.status}`));
    return null;
  }
  const contentType = response.headers.get('Content-Type') || '';
  if (!contentType.includes('application/json')) {
    console.error(new Error(`Fetch notifications failed: Unexpected content type "${contentType}"`));
    return null;
  }
  // parse response
  try {
    return await response.json();
  } catch (error) {
    console.error(new Error('Fetch notifications failed: Parsing error', { cause: error }));
    return null;
  }
}

export default async function initClientSideNotifications() {
  const notificationProps = getNotificationsProps();
  const desktopPopover = document.querySelector('#notifications-popover');
  const mobilePopover = document.querySelector('#notifications-mobile-popover');

  if (notificationProps && desktopPopover && mobilePopover) {
    const json = await fetchNotificationCenter();
    if (!json) {
      return;
    }
    try {
      const { sections, badge, messageCount } = json;
      const props = {
        ...notificationProps,
        sections,
        notificationCount: badge,
        messageCount,
      };
      const desktopContents = React.createElement(NotificationPopoverContents, props, null);
      const mobileContents = React.createElement(MobileNotificationPopoverContents, props, null);
      desktopPopover.innerHTML = '';
      mobilePopover.innerHTML = '';
      const desktopRoot = createRoot(desktopPopover);
      const mobileRoot = createRoot(mobilePopover);

      desktopRoot.render(desktopContents);
      mobileRoot.render(mobileContents);

      setTimeout(() => {
        finishInitClientSideNotifications({ userKey: props.userKey, badge, messageCount });
      }, 1);
    } catch (error) {
      console.error(new Error('Failed to render client-side notifications', { cause: error }));
    }
  }
}

function finishInitClientSideNotifications({ userKey, badge, messageCount }) {
  // Logic to finish initializing notification popovers
  try {
    updateBadge(userKey, badge + messageCount);
    checkInitNotificationPopovers();
    // if notifications is already open
    if (document.querySelector('#notifications-popover.cw_popoverIsOpen,#notifications-mobile.vx_modalIsOpen')) {
      window.dispatchEvent(new CustomEvent('CWNotificationsPopover'));
    }
  } catch (initError) {
    console.error(new Error('Failed to finish initializing client-side notifications', { cause: initError }));
  }
}

export function getCachedBadge(userKey) {
  const item = window.sessionStorage.getItem(NOTIFICATION_CENTER_BADGE_CACHE_KEY);
  if (item) {
    const [savedUserKey, savedBadge] = item.split(':');
    if (savedUserKey === userKey) {
      return Number(savedBadge) || 0;
    }
  }
  return 0;
}

export function setBadgeCache(userKey, badge) {
  window.sessionStorage.setItem(NOTIFICATION_CENTER_BADGE_CACHE_KEY, `${userKey}:${badge}`);
}

export function updateBadge(userKey, badge) {
  setBadgeCache(userKey, badge);
  const badges = document.querySelectorAll('#notification-center-badge,#notification-center-mobile-badge');
  badges.forEach((badgeElem: HTMLElement) => {
    badgeElem.innerHTML = badge;
    badgeElem.style.display = badge ? '' : 'none';
  });
}

export function checkInitNotificationPopovers() {
  const limit = 10 * 60 * 2; // 10 min
  let count = 0;
  let intervalId = null;

  const check = () => {
    if (
      !(
        document.querySelector(DISMISS_NOTIFICATIONS_MODAL_ICON) &&
        document.querySelector(`#${NOTIFICATIONS_MOBILE_POPOVER}`) &&
        document.querySelector(`#${NOTIFICATIONS_POPOVER}`) &&
        document.querySelector(`#${NOTIFICATIONS_MOBILE_POPOVER} .cw_popover-body`) &&
        document.querySelector(`#${NOTIFICATIONS_MOBILE_POPOVER} #cw_tab-notifications`) &&
        document.querySelector(`#${NOTIFICATIONS_MOBILE_POPOVER} #cw_tab-messages`) &&
        document.querySelector(`#${NOTIFICATIONS_POPOVER} .cw_popover-body`) &&
        document.querySelector(`#${NOTIFICATIONS_POPOVER} #cw_tab-notifications`) &&
        document.querySelector(`#${NOTIFICATIONS_POPOVER} #cw_tab-messages`)
      )
    ) {
      count++;
      if (count > limit) {
        clearInterval(intervalId);
        return true;
      }
      return false;
    }
    initNotificationPopovers();
    clearInterval(intervalId);
    return true;
  };
  if (!check()) {
    intervalId = setInterval(check, 500);
  }
  return intervalId;
}
